import React, { useEffect, useState } from "react";
import Form from "react-bootstrap/Form";

import DstButton from "../../../../components/DstButton/DstButton";
import MultiSelectDropdown from "../../../../components/MultiSelectDropdown/MultiSelectDropdown";
import RequiredLabel from "../../../../components/RequiredLabel/RequiredLabel";
import { useSession } from "../../../../contexts/SessionContext";
import { CohortIntensity } from "../../../../models/CohortIntensity";
import { CustomerType } from "../../../../models/CustomerType";
import translate from "../../../../services/Translate";
import { fetchCohortsLeaders, fetchCohortsManagers } from "../../Cohorts.function";
import { createCohort } from "./CohortGeneralForm.function";

const CohortGeneralForm = ({ onSuccess, onCancel }: { onSuccess: () => void; onCancel: () => void }) => {
  const { language } = useSession();

  const [form, setForm] = useState({
    name: "",
    description: "",
    intensity: null as CohortIntensity | null,
    language: "",
    leader: null as number | null,
    program_manager: null as number | null,
    school: 1,
    customer_type: null as CustomerType | null,
    need_project: false,
    dt_start: "",
    dt_end_planned: "",
    dt_end: "",
    logo: "",
  });
  const [leaders, setLeaders] = useState<any[]>([]);
  const [managers, setManagers] = useState<any[]>([]);
  const [loadingLeaders, setLoadingLeaders] = useState(true);
  const [loadingManagers, setLoadingManagers] = useState(true);

  const [loading, setLoading] = useState(false);

  const _handleChange = (key: string, value: any) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const _isFormValid =
    form.name.trim() !== "" &&
    form.description.trim() !== "" &&
    form.intensity !== null &&
    form.language !== "" &&
    form.leader !== null &&
    form.school !== null &&
    form.customer_type !== null &&
    form.dt_end !== "";

  const _handleSubmit = () => {
    if (!_isFormValid) return;

    setLoading(true);

    createCohort(
      language,
      {
        name: form.name,
        description: form.description,
        intensity: form.intensity!,
        language: form.language,
        leader_id: form.leader!,
        program_manager_id: form.program_manager ?? undefined,
        school: form.school,
        customer_type: form.customer_type!,
        dt_end: form.dt_end,
        dt_start: form.dt_start || undefined,
        dt_end_planned: form.dt_end_planned || undefined,
        need_project: form.need_project,
        logo: form.logo || undefined,
      },
      onSuccess,
      () => setLoading(false)
    );
  };

  useEffect(() => {
    fetchCohortsLeaders(language, setLeaders, true, () => setLoadingLeaders(false));
    fetchCohortsManagers(language, setManagers, true, () => setLoadingManagers(false));
  }, [language]);

  return (
    <Form className="p-3">
      <Form.Group className="mb-3">
        <Form.Label>
          <RequiredLabel label={translate(language, "PAGES.COHORTS.CREATE_COHORT.NAME")} />
        </Form.Label>
        <Form.Control value={form.name} onChange={(event) => _handleChange("name", event.target.value)} />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>
          <RequiredLabel label={translate(language, "GLOBAL.DESCRIPTION")} />
        </Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          value={form.description}
          onChange={(event) => _handleChange("description", event.target.value)}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>
          <RequiredLabel label={translate(language, "GLOBAL.LANGUAGE")} />
        </Form.Label>
        <Form.Select value={form.language} onChange={(event) => _handleChange("language", event.target.value)}>
          <option value="" disabled>
            -- {translate(language, "PAGES.COHORTS.CREATE_COHORT.SELECT")} --
          </option>
          <option value="fr-FR">{translate(language, "PAGES.COHORTS.CREATE_COHORT.LANGUAGE.OPTIONS.FR")}</option>
          <option value="en-US">{translate(language, "PAGES.COHORTS.CREATE_COHORT.LANGUAGE.OPTIONS.US")}</option>
        </Form.Select>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>
          <RequiredLabel label={translate(language, "PAGES.COHORTS.CREATE_COHORT.INTENSITY")} />
        </Form.Label>
        <Form.Select
          value={form.intensity ?? ""}
          onChange={(event) => _handleChange("intensity", event.target.value as CohortIntensity)}
        >
          <option value="" disabled>
            -- {translate(language, "PAGES.COHORTS.CREATE_COHORT.SELECT")} --
          </option>
          {Object.values(CohortIntensity).map((intensity) => (
            <option key={intensity} value={intensity}>
              {intensity}
            </option>
          ))}
        </Form.Select>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>
          <div className="w-100">
            <RequiredLabel label={translate(language, "PAGES.COHORTS.CREATE_COHORT.LEADER_LABEL")} />
          </div>
        </Form.Label>

        <MultiSelectDropdown
          btnWidth="100%"
          items={leaders}
          mock={loadingLeaders}
          isSingleSelect
          badgeItemProperty="full_name"
          language={language}
          label={
            form.leader
              ? leaders.find((leader) => leader.staff_member_id === form.leader)?.full_name
              : translate(language, "PAGES.COHORTS.CREATE_COHORT.LEADER_PLACEHOLDER")
          }
          displayItem={(item) => item.full_name}
          onConfirm={(selected) => _handleChange("leader", selected.length ? selected[0].staff_member_id : null)}
          toggleClass="w-100"
          borderToggle
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>{translate(language, "GLOBAL.PROGRAM_MANAGER")}</Form.Label>

        <MultiSelectDropdown
          btnWidth="100%"
          items={managers}
          mock={loadingManagers}
          isSingleSelect
          isSingleSelectRemovable
          badgeItemProperty="full_name"
          language={language}
          label={
            form.program_manager
              ? managers.find((manager) => manager.staff_member_id === form.program_manager)?.full_name
              : translate(language, "PAGES.COHORTS.CREATE_COHORT.PROGRAM_MANAGER_PLACEHOLDER")
          }
          displayItem={(item) => item.full_name}
          onConfirm={(selected) =>
            _handleChange("program_manager", selected.length ? selected[0].staff_member_id : null)
          }
          toggleClass="w-100"
          borderToggle
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>
          <RequiredLabel label={translate(language, "PAGES.COHORTS.CREATE_COHORT.SCHOOL")} />
        </Form.Label>
        <Form.Select value={form.school} onChange={(event) => _handleChange("school", Number(event.target.value))}>
          <option value={1}>Datascientest</option>
          <option value={2}>CyberUniversity</option>
        </Form.Select>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>
          <RequiredLabel label={translate(language, "PAGES.COHORTS.CREATE_COHORT.CUSTOMER_TYPE")} />
        </Form.Label>
        <Form.Select
          value={form.customer_type ?? ""}
          onChange={(event) => _handleChange("customer_type", event.target.value as CustomerType)}
        >
          <option value="" disabled>
            -- {translate(language, "PAGES.COHORTS.CREATE_COHORT.SELECT")} --
          </option>

          {Object.values(CustomerType).map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </Form.Select>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Check
          type="switch"
          label={translate(language, "PAGES.COHORTS.CREATE_COHORT.NEED_PROJECT")}
          checked={form.need_project}
          onChange={(event) => _handleChange("need_project", event.target.checked)}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>{translate(language, "GLOBAL.START_DATE")}</Form.Label>
        <Form.Control
          type="date"
          value={form.dt_start}
          onChange={(event) => _handleChange("dt_start", event.target.value)}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>{translate(language, "PAGES.COHORTS.CREATE_COHORT.END_DATE_PLANNED")}</Form.Label>
        <Form.Control
          type="date"
          value={form.dt_end_planned}
          onChange={(event) => _handleChange("dt_end_planned", event.target.value)}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>
          <RequiredLabel label={translate(language, "GLOBAL.END_DATE")} />
        </Form.Label>
        <Form.Control
          type="date"
          value={form.dt_end}
          onChange={(event) => _handleChange("dt_end", event.target.value)}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>{translate(language, "PAGES.COHORTS.CREATE_COHORT.LOGO")}</Form.Label>
        <Form.Control
          type="url"
          placeholder="https://..."
          value={form.logo}
          onChange={(event) => _handleChange("logo", event.target.value)}
        />
      </Form.Group>

      <div className="d-flex justify-content-end">
        <DstButton
          value={translate(language, "GLOBAL.CANCEL")}
          variant="light"
          btnClass="me-2"
          clickFunction={onCancel}
        />
        <DstButton
          value={translate(language, "PAGES.COHORTS.CREATE_COHORT.BUTTON.CREATE")}
          variant="primary"
          loading={loading}
          disabled={!_isFormValid}
          clickFunction={_handleSubmit}
        />
      </div>
    </Form>
  );
};

export default CohortGeneralForm;
